import React, { ErrorInfo, ReactNode } from 'react';
import { RefreshCw, AlertTriangle } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Unhandled React Error caught by ErrorBoundary:', error, errorInfo);
    this.setState({ errorInfo });
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleResetCache = () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (e) {
      console.warn('Could not clear web storage:', e);
    }
    window.location.href = '/';
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#FAF8F5] text-[#2c221e] flex items-center justify-center p-6 font-sans">
          <div className="max-w-xl w-full bg-[#fbf9f4] border-2 border-[#d8ceb9] rounded-3xl p-8 shadow-lg space-y-6">
            <div className="flex items-center gap-3 text-[#7a5b3a]">
              <div className="p-3 bg-[#e8dec9] rounded-2xl">
                <AlertTriangle className="w-6 h-6 text-[#7a5b3a]" />
              </div>
              <div>
                <h1 className="font-serif text-2xl font-bold text-[#2c221e]">
                  Family Archive Reader
                </h1>
                <p className="text-xs text-[#7a5b3a] uppercase tracking-wider font-semibold">
                  Page Display Notice
                </p>
              </div>
            </div>

            <p className="text-base text-[#4a3525] leading-relaxed">
              We encountered a temporary browser compatibility or loading issue while rendering this page.
            </p>

            {this.state.error && (
              <div className="p-4 rounded-xl bg-[#f3ede0] border border-[#d8ceb9] text-xs font-mono text-[#5c4328] overflow-x-auto max-h-48">
                <p className="font-bold mb-1">{this.state.error.toString()}</p>
                {this.state.errorInfo && (
                  <pre className="text-[11px] whitespace-pre-wrap">
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 pt-2">
              <button
                onClick={this.handleReload}
                className="px-5 py-2.5 rounded-xl bg-[#7a5b3a] hover:bg-[#5c4328] text-white font-bold text-sm transition flex items-center gap-2 cursor-pointer shadow-xs"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Reload Page</span>
              </button>

              <button
                onClick={this.handleResetCache}
                className="px-4 py-2.5 rounded-xl bg-[#e8dec9] hover:bg-[#d8ceb9] text-[#2c221e] font-semibold text-sm transition cursor-pointer"
              >
                Clear Cache & Return to Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
